import {
  //reference for remirror inons: https://www.remirror.io/docs/api/icons/
  Suspense,
  useCallback,
  useRef,
  useState,
} from "react";
import {
  useActive,
  useAttrs,
  useChainedCommands,
  useCommands,
  useRemirrorContext,
} from "@remirror/react";
import { Divider, Button, Box, ToggleButton, Tooltip } from "@mui/material";
import { useDialogs } from "../../contexts/useDialogs";
import { IconTypes } from "../icons.types";
import FormatLineSpacingIcon from "@mui/icons-material/FormatLineSpacing";
import FormatColorTextIcon from "@mui/icons-material/FormatColorText";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import PushPinIcon from "@mui/icons-material/PushPin";
import {
  FontFamilyButton,
  FontSizeButton,
  ColorButton,
  YoutubeButton,
} from "./components/buttons";
import { LinkButton } from "./components/link-button";
import { TableButtons } from "./components/table-buttons";
import { insertFieldToEditor } from "./editor-func";
import { useHtmlEditorContext } from "./useHtmlEditorContext";
import { devWarn } from "../../services/util";
import {
  CommandMenuItem,
  CommandButtonGroup,
  ToggleBoldButton,
  ToggleItalicButton,
  DropdownButton,
  CommandButton,
  LeftAlignButton,
  CenterAlignButton,
  RightAlignButton,
  ToggleHeadingMenuItem,
  UndoButton,
  RedoButton,
} from "@remirror/react-ui";

const EditorCommandMenu = ({ name, ext }: { name: string; ext: string }) => {
  const commands = useCommands();
  const command = commands[name];
  const active = useActive()[ext]?.();
  if (!command) {
    return null;
  }
  return (
    <CommandMenuItem
      commandName={name}
      enabled={command.enabled?.()}
      active={active}
      onSelect={command}
      displayDescription={false}
    />
  );
};

// const ClearContentButton = ({ onClear }: { onClear: () => void }) => (
//   <CommandButton
//     commandName="clearContent"
//     label="Clear Content"
//     icon="deleteBinLine"
//     enabled={true}
//     onSelect={onClear}
//   />
// );

export type HtmlEditorMenuProps = {
  embedded?: boolean;
  compact?: boolean;
  disableExternalEdit?: boolean;
  onExternalEdit?: (html: string) => void;
  editorId: string;
  onSwitchToHtml?: () => void;
  /** Pin current mode as global default for all editors */
  onPinMode?: () => void;
  /** Whether current mode matches the global default */
  isDefaultMode?: boolean;
};

export const HtmlEditorMenu = ({
  embedded,
  compact: forceCompact,
  disableExternalEdit,
  onExternalEdit,
  editorId,
  onSwitchToHtml,
  onPinMode,
  isDefaultMode,
}: HtmlEditorMenuProps) => {
  const { isSourceEdit, toHtml, darkBkg, setDarkBkg, fields, fieldArgs } =
    useHtmlEditorContext();

  const [autoNarrow, setAutoNarrow] = useState(false);
  const [autoCompact, setAutoCompact] = useState(false);

  // Use either forced compact mode from props or auto-detected compact mode
  const isCompact = forceCompact || autoCompact;
  // For non-compact mode, still use narrow layout if space is limited
  const isNarrow = !isCompact && autoNarrow;

  const { commands } = useRemirrorContext();
  const { openImageModal, openHtmlEditorModal } = useDialogs();

  // Callback ref: fires when DOM element is actually attached
  // (unlike useEffect, which fires before Remirror mounts children)
  const observerRef = useRef<ResizeObserver | null>(null);
  const ref = useCallback((node: HTMLDivElement | null) => {
    // Cleanup old observer
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    if (node) {
      const updateWidth = () => {
        const width = node.offsetWidth;
        if (width > 0) {
          setAutoCompact(width < 500);
          setAutoNarrow(width < 900);
        }
      };

      // Measure immediately on attach
      updateWidth();

      // Watch for resizes
      observerRef.current = new ResizeObserver(updateWidth);
      observerRef.current.observe(node);
    }
  }, []);

  const chain = useChainedCommands();

  const attrs = useAttrs();

  const clearMarkup = useCallback(
    () => commands.removeMark({ type: null }),
    [commands],
  );

  const handleExternalEdit = (editorId: string) => {
    const htmlString = toHtml();
    openHtmlEditorModal({
      value: htmlString,
      onChange: onExternalEdit,
      // without these the popup silently loses the Expressions menu
      fields,
      fieldArgs,
      editorId: "external-" + editorId,
    });
  };

  /**
   * HTML snippets (the variants table) carry the comment-wrapped
   * `<!--{{Variants}}-->` iterator. ProseMirror must never be handed that as
   * text — `insertText` would land it as literal characters. Append it to the
   * editor's *content* instead: the value round-trips through the cmCode string
   * handler, which turns the comments into `data-expr-*` attributes on the
   * variant `<tr>` and back again on save. The user stays in the visual editor
   * and sees a real table.
   */
  const handleHtmlSnippet = (snippet: string) => {
    if (!onExternalEdit) {
      devWarn("HtmlEditorMenu: no content sink for an HTML snippet");
      return;
    }
    const htmlString = toHtml() ?? "";
    onExternalEdit(
      htmlString +
        (htmlString && !htmlString.endsWith("\n") ? "\n" : "") +
        snippet
    );
  };

  const insertField = (content: string) =>
    insertFieldToEditor(
      content,
      fieldArgs,
      { chain, insertText: commands.insertText },
      handleHtmlSnippet
    );

  // Render compact mode with minimal buttons
  const renderCompactMode = () => (
    <>
      <CommandButtonGroup className="btn-group">
        <ToggleBoldButton />
        <ToggleItalicButton />
        <LinkButton />
        {/* <ClearContentButton onClear={() => setContent("")} /> */}
        {fields && (
          <DropdownButton
            aria-label="Expressions"
            color="secondary"
            icon="bracesLine"
          >
            {fields.map((f) =>
              f.name ? (
                <CommandMenuItem
                  key={f.key}
                  onSelect={() => insertField(f.content)}
                  displayDescription={!!f.desc}
                  description={f.desc}
                  label={f.name}
                  commandName="insertText"
                  enabled={true}
                />
              ) : (
                <Divider key={f.key} />
              )
            )}
          </DropdownButton>
        )}
      </CommandButtonGroup>

      {onSwitchToHtml && (
        <CommandButtonGroup className="btn-group">
          <CommandButton
            commandName="htmlMode"
            label="Switch to HTML mode"
            icon="codeLine"
            enabled={true}
            onSelect={() => onSwitchToHtml?.()}
          />
        </CommandButtonGroup>
      )}

      {/* Mode indicator + pin */}
      <Tooltip
        title={
          isDefaultMode
            ? "Visual is your default mode (click to switch default to HTML)"
            : "Set Visual as default for all editors"
        }
      >
        <Box
          onClick={onPinMode}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.25,
            px: 0.5,
            py: 0.25,
            borderRadius: 1,
            border: "1px solid",
            borderColor: isDefaultMode ? "primary.main" : "divider",
            bgcolor: isDefaultMode ? "rgba(25, 118, 210, 0.08)" : "transparent",
            color: isDefaultMode ? "primary.main" : "text.secondary",
            cursor: onPinMode ? "pointer" : "default",
            ml: -0.5,
          }}
        >
          <FormatColorTextIcon sx={{ fontSize: 14 }} />
          {isDefaultMode ? (
            <PushPinIcon sx={{ fontSize: 13 }} />
          ) : (
            <PushPinOutlinedIcon sx={{ fontSize: 13 }} />
          )}
        </Box>
      </Tooltip>

      <Box sx={{ flexGrow: 1 }} />

      {!disableExternalEdit && (embedded || isCompact) && onExternalEdit && (
        <Button
          size="small"
          variant="outlined"
          color="mute"
          startIcon={<IconTypes.OpenInNew />}
          onClick={() => handleExternalEdit(editorId)}
          sx={{
            color: "text.secondary",
            borderColor: "text.secondary",
            "&:hover": {
              borderColor: "text.primary",
              color: "text.primary",
            },
          }}
        >
          Full Editor
        </Button>
      )}
    </>
  );

  return (
    <Suspense>
      <div ref={ref}>
        <Box
          className="editor-toolbar"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            minHeight: "48px",
            px: 0,
          }}
        >
          {!isSourceEdit && (
            <>
              {isCompact ? (
                renderCompactMode()
              ) : (
                <>
                  <CommandButtonGroup className="btn-group">
                    <FontFamilyButton />
                    <FontSizeButton />
                    <ColorButton
                      name="textColor"
                      color={attrs.textColor()?.color as string}
                      setColor={(color) => {
                        commands.setTextColor(color);
                      }}
                      icon="fontColor"
                      label="Text Color"
                    />
                    <ColorButton
                      name="bgColor"
                      color={attrs.textBgColor()?.bgColor as string}
                      setColor={(color) => {
                        commands.setTextBgColor(color);
                      }}
                      icon="markPenLine"
                      label="Text Background Color"
                    />
                  </CommandButtonGroup>

                  <CommandButtonGroup className="btn-group">
                    <ToggleBoldButton />
                    <ToggleItalicButton />
                    <DropdownButton
                      aria-label="More font styles"
                      color="secondary"
                    >
                      <EditorCommandMenu
                        name="toggleUnderline"
                        ext="underline"
                      />
                      <EditorCommandMenu name="toggleStrike" ext="strike" />
                      <EditorCommandMenu name="toggleSubscript" ext="sub" />
                      <EditorCommandMenu name="toggleSuperscript" ext="sup" />
                    </DropdownButton>
                  </CommandButtonGroup>

                  <CommandButtonGroup className="btn-group">
                    {fields && (
                      <DropdownButton
                        aria-label="Expressions"
                        color="secondary"
                        icon="bracesLine"
                      >
                        {fields.map((f) =>
                          f.name ? (
                            <CommandMenuItem
                              key={f.key}
                              onSelect={() => insertField(f.content)}
                              displayDescription={!!f.desc}
                              description={f.desc}
                              label={f.name}
                              commandName="insertText"
                              enabled={true}
                            />
                          ) : (
                            <Divider key={f.key} />
                          )
                        )}
                      </DropdownButton>
                    )}
                    <LinkButton />
                    <CommandButton
                      commandName="insertImage"
                      active={false}
                      enabled={true}
                      icon="imageLine"
                      label="Insert image"
                      onSelect={() => {
                        openImageModal(
                          {
                            onSelect: (_p, url) => {
                              commands.insertImage({ src: url });
                            },
                            canChange: true,
                            canSelectFolder: true,
                          },
                          "select",
                        );
                      }}
                    />
                    <TableButtons />
                    <DropdownButton aria-label="More embeds" color="secondary">
                      <YoutubeButton />
                    </DropdownButton>
                  </CommandButtonGroup>

                  <CommandButtonGroup className="btn-group">
                    {!isNarrow && (
                      <>
                        <LeftAlignButton />
                        <CenterAlignButton />
                        <RightAlignButton />
                      </>
                    )}
                    <DropdownButton
                      aria-label="More text formatting"
                      icon={isNarrow ? "paragraph" : null}
                      color="secondary"
                    >
                      {isNarrow && (
                        <EditorCommandMenu name="leftAlign" ext="node" />
                      )}
                      {isNarrow && (
                        <EditorCommandMenu name="centerAlign" ext="node" />
                      )}
                      {isNarrow && (
                        <EditorCommandMenu name="rightAlign" ext="node" />
                      )}
                      <EditorCommandMenu name="increaseIndent" ext="node" />
                      <EditorCommandMenu name="decreaseIndent" ext="node" />
                      <Divider />
                      <CommandMenuItem
                        icon={<FormatLineSpacingIcon fontSize="small" />}
                        onSelect={() => {
                          commands.setBottomPadding(0);
                        }}
                        displayDescription={false}
                        label="Condense paragraphs"
                        commandName="insertText"
                        enabled={true}
                      />
                      <Divider />
                      <EditorCommandMenu
                        name="toggleOrderedList"
                        ext="orderedList"
                      />
                      <EditorCommandMenu
                        name="toggleBulletList"
                        ext="bulletList"
                      />
                      <Divider />
                      <ToggleHeadingMenuItem
                        attrs={{ level: 1 }}
                        label="Heading 1"
                      />
                      <ToggleHeadingMenuItem
                        attrs={{ level: 2 }}
                        label="Heading 2"
                      />
                      <ToggleHeadingMenuItem
                        attrs={{ level: 3 }}
                        label="Heading 3"
                      />
                    </DropdownButton>
                  </CommandButtonGroup>
                </>
              )}
            </>
          )}

          {!isSourceEdit && !isCompact && (
            <Box
              sx={{
                flexGrow: 1,
                mr: 1,
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
              }}
            >
              <CommandButtonGroup className="btn-group">
                <CommandButton
                  commandName="removeFormat"
                  label="Clear formatting"
                  icon="formatClear"
                  enabled={true}
                  onSelect={clearMarkup}
                />
                {/* <ClearContentButton onClear={() => setContent("")} /> */}
                {isNarrow && (
                  <DropdownButton aria-label="More commands" color="secondary">
                    <EditorCommandMenu name="undo" ext="strike" />
                    <EditorCommandMenu name="redo" ext="sub" />
                    {onSwitchToHtml && (
                      <CommandMenuItem
                        commandName="htmlMode"
                        enabled={true}
                        onSelect={() => onSwitchToHtml()}
                        label="Switch to HTML mode"
                        icon="codeLine"
                      />
                    )}
                    {onPinMode && (
                      <CommandMenuItem
                        commandName="pinMode"
                        enabled={true}
                        onSelect={onPinMode}
                        label={
                          isDefaultMode
                            ? "Visual is default (click to switch to HTML)"
                            : "Set Visual as default for all editors"
                        }
                        icon={
                          isDefaultMode ? (
                            <PushPinIcon sx={{ fontSize: 16 }} />
                          ) : (
                            <PushPinOutlinedIcon sx={{ fontSize: 16 }} />
                          )
                        }
                      />
                    )}
                  </DropdownButton>
                )}
              </CommandButtonGroup>
              {!isNarrow && (
                <>
                  <CommandButtonGroup className="btn-group">
                    <UndoButton />
                    <RedoButton />
                  </CommandButtonGroup>
                  <CommandButtonGroup className="btn-group">
                    <ToggleButton
                      size="small"
                      value="bkg"
                      selected={darkBkg}
                      onChange={() => {
                        setDarkBkg(!darkBkg);
                      }}
                      title="Toggle dark background"
                      sx={{
                        "&.MuiToggleButton-root.Mui-selected": {
                          color: "#eee",
                          backgroundColor: "#777",
                          "&:hover": {
                            backgroundColor: "#666",
                          },
                        },
                        svg: {
                          width: 14,
                          height: 14,
                        },
                      }}
                    >
                      {darkBkg ? (
                        <IconTypes.LightMode />
                      ) : (
                        <IconTypes.DarkMode />
                      )}
                    </ToggleButton>

                    {/* {!embedded && (
                      <CommandButton
                        commandName="fullscreenEdit"
                        label={
                          fullScreen
                            ? "Exit full screen edit"
                            : "Full screen edit"
                        }
                        icon={
                          fullScreen ? "fullscreenExitLine" : "fullscreenLine"
                        }
                        enabled={true}
                        onSelect={() => setFullScreen?.(!fullScreen)}
                      />
                    )} */}
                  </CommandButtonGroup>
                  {onSwitchToHtml && (
                    <CommandButtonGroup className="btn-group">
                      <CommandButton
                        commandName="htmlMode"
                        label="Switch to HTML mode"
                        icon="codeLine"
                        enabled={true}
                        onSelect={() => onSwitchToHtml()}
                      />
                    </CommandButtonGroup>
                  )}
                  {/* Mode indicator + pin */}
                  <Tooltip
                    title={
                      isDefaultMode
                        ? "Visual is your default mode (click to switch default to HTML)"
                        : "Set Visual as default for all editors"
                    }
                  >
                    <Box
                      onClick={onPinMode}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        px: 0.75,
                        py: 0.25,
                        borderRadius: 1,
                        border: "1px solid",
                        borderColor: isDefaultMode ? "primary.main" : "divider",
                        bgcolor: isDefaultMode
                          ? "rgba(25, 118, 210, 0.08)"
                          : "transparent",
                        color: isDefaultMode
                          ? "primary.main"
                          : "text.secondary",
                        cursor: onPinMode ? "pointer" : "default",
                        transition: "all 0.15s",
                        "&:hover": onPinMode
                          ? {
                              borderColor: "primary.main",
                              color: "primary.main",
                            }
                          : {},
                      }}
                    >
                      <FormatColorTextIcon sx={{ fontSize: 14 }} />
                      {isDefaultMode ? (
                        <PushPinIcon sx={{ fontSize: 13 }} />
                      ) : (
                        <PushPinOutlinedIcon sx={{ fontSize: 13 }} />
                      )}
                    </Box>
                  </Tooltip>
                </>
              )}
              {!disableExternalEdit && onExternalEdit && (
                <CommandButton
                  commandName="externalEdit"
                  label="Edit in popup"
                  icon="fullscreenLine"
                  enabled={true}
                  onSelect={() => handleExternalEdit(editorId)}
                />
              )}
            </Box>
          )}
        </Box>
      </div>
    </Suspense>
  );
};
